/**
 * Keeps the filter dropdown menus OUT of the horizontally-scrolling filters row.
 *
 * Why: `.filters-row` scrolls horizontally, which makes it a real scroll
 * container whenever the pills overflow (on /user/hikes they do —
 * scrollWidth 1084 vs clientWidth 969). WebKit mispaints `position: fixed`
 * descendants of a composited scroll container, so each Popper-positioned
 * `.dropdown-menu` opened with correct geometry, hit-testing and
 * IntersectionObserver ratio yet was never painted: in Safari every filter
 * dropdown looked completely dead, while Chrome was fine. Popper already applies
 * a `translate3d` to the menu, so layer promotion is not a workaround here.
 *
 * Moving each menu into a sibling layer outside the scroller removes the
 * offending ancestor entirely, so the row can still scroll on one line.
 *
 * Bootstrap's Dropdown caches its menu element when the instance is constructed,
 * so this must run AFTER `getOrCreateInstance`; the cached reference survives the
 * move, and Popper positions against the toggle rather than the DOM parent.
 */

const TOGGLE = '[data-bs-toggle="dropdown"]';

/** Pairs a moved menu back to its toggle so orphans can be reaped. */
const ID_ATTR = 'filterMenuId';
const FOR_ATTR = 'filterMenuFor';

function toggleId(toggle: HTMLElement, index: number): string {
  if (!toggle.dataset[ID_ATTR]) toggle.dataset[ID_ATTR] = `f${index}`;
  return toggle.dataset[ID_ATTR] as string;
}

/**
 * Moves every filter menu in `row` into `layer`. Idempotent: a menu already in
 * the layer is left alone. Menus whose toggle has since left the row are removed,
 * so a `v-if`'d filter (e.g. the custom-tags one) can't leak a stale menu.
 *
 * @returns the number of menus newly moved.
 */
export function relocateFilterMenus(row: HTMLElement | null, layer: HTMLElement | null): number {
  if (!row || !layer) return 0;

  const liveIds = new Set(
    Array.from(row.querySelectorAll<HTMLElement>(TOGGLE))
      .map((t) => t.dataset[ID_ATTR])
      .filter((id): id is string => !!id),
  );
  for (const child of Array.from(layer.children)) {
    const owner = (child as HTMLElement).dataset[FOR_ATTR];
    if (!owner || !liveIds.has(owner)) child.remove();
  }

  let moved = 0;
  Array.from(row.querySelectorAll<HTMLElement>(TOGGLE)).forEach((toggle, index) => {
    const wrapper = toggle.parentElement;
    if (!wrapper) return;
    const menu = wrapper.querySelector<HTMLElement>(':scope > .dropdown-menu');
    if (!menu) return; // already relocated, or this toggle has no menu
    menu.dataset[FOR_ATTR] = toggleId(toggle, index);
    layer.appendChild(menu);
    moved += 1;
  });
  return moved;
}
